<script>
import MottoIcon from '@components/motto-icon.vue'
import { Cropper, Preview } from 'vue-advanced-cropper'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import CropZoomResizer from '@components/crop-zoom-resizer.vue'
import 'vue-advanced-cropper/dist/style.css'
import LoaderIcon from '@components/loader-icon.vue'
import { updateProfilePhoto } from '@src/api/personnelManagement'

export default {
  components: {
    MottoIcon,
    Cropper,
    Preview,
    CropZoomResizer,
    LoaderIcon,
  },
  mixins: [isMobileMixin],

  props: {
    userId: {
      type: Number,
      required: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isSubmitting: false,
      imageLoaded: false,
      imagePreviewPath: '',
      zoom: 0,
      error: false,
      errorMessage: null,
      previewImageResult: {
        coordinates: null,
        image: null,
      },
    }
  },

  methods: {
    previewImage(event) {
      this.$bvModal.show('previewPictureUpload')
      const file = event.target.files[0]
      if (!file.type.startsWith('image/')) {
        this.error = true
        this.errorMessage = 'Only images are allowed'
        return
      }

      if (file.size > 5242880) {
        this.error = true
        this.errorMessage = 'Picture size limit exceeded. Please select an image file under 5MB.'
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreviewPath = e.target.result
        this.imageLoaded = true
      }
      reader.readAsDataURL(file)
    },

    defaultSize({ imageSize }) {
      return {
        width: Math.min(imageSize.height, imageSize.width),
        height: Math.min(imageSize.height, imageSize.width),
      }
    },
    stencilSize({ boundaries }) {
      return {
        width: Math.min(boundaries.height, boundaries.width),
        height: Math.min(boundaries.height, boundaries.width),
      }
    },
    onChange() {
      const cropper = this.$refs.cropper
      if (cropper) {
        const { image, coordinates, imageSize } = cropper
        if (imageSize.width / imageSize.height > coordinates.width / coordinates.height) {
          // Determine the position of slider bullet
          // It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
          this.zoom =
            (cropper.imageSize.height - cropper.coordinates.height) /
            (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
        } else {
          this.zoom =
            (cropper.imageSize.width - cropper.coordinates.width) /
            (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
        }
        this.previewImageResult = {
          coordinates,
          image,
        }
      }
    },
    onZoom(value) {
      const cropper = this.$refs.cropper
      if (cropper) {
        if (cropper.imageSize.height < cropper.imageSize.width) {
          const minHeight = cropper.sizeRestrictions.minHeight
          const imageHeight = cropper.imageSize.height
          // Determine the current absolute zoom and the new absolute zoom
          // to calculate the sought relative zoom value
          cropper.zoom(
            (imageHeight - this.zoom * (imageHeight - minHeight)) / (imageHeight - value * (imageHeight - minHeight))
          )
        } else {
          const minWidth = cropper.sizeRestrictions.minWidth
          const imageWidth = cropper.imageSize.width
          cropper.zoom(
            (imageWidth - this.zoom * (imageWidth - minWidth)) / (imageWidth - value * (imageWidth - minWidth))
          )
        }
      }
    },
    clearFields() {
      this.imageLoaded = false
      this.imagePreviewPath = ''
      this.zoom = 0
      this.errorMessage = null
      this.error = false
      this.isSubmitting = false
      this.$refs.inputPictureUpload.value = ''
      this.previewImageResult = {
        coordinates: null,
        image: null,
      }
    },

    async submitPicture() {
      try {
        this.isSubmitting = true
        const { canvas } = this.$refs.cropper.getResult()
        const formData = new FormData()
        const blob = await new Promise((resolve) =>
          canvas.toBlob((blob) => {
            resolve(blob)
          }, 'image/jpeg')
        )
        formData.append('file', blob, `${this.userId}_profile_photo`)
        const { data } = await updateProfilePhoto({
          formData,
          userId: this.userId,
        })
        this.$root.$bvToast.toast('Photo successfully updated', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-success',
        })
        this.$emit('update-photo', data)
        this.$bvModal.hide('previewPictureUpload')
      } catch (e) {
        this.$root.$bvToast.toast('There was an error trying to upload your picture, please contact support', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<template>
  <div>
    <label v-if="editable" :class="$style.pictureUploadLabel" class="custom-file-upload">
      <input
        ref="inputPictureUpload"
        type="file"
        name="img"
        accept="image/x-png,image/gif,image/jpeg,image/png"
        @change="previewImage" />
      <MottoIcon :class="$style.penIcon" name="PenToSquare" color="primary" />
    </label>
    <b-modal
      id="previewPictureUpload"
      modal-class="modal-picture-upload"
      hide-footer
      :centered="true"
      @hide="clearFields">
      <LoaderIcon v-if="!imageLoaded || error" :error="error" :error-message="errorMessage" class="m-auto"></LoaderIcon>
      <div v-if="imageLoaded" :class="$style.previewContainer">
        <div :class="$style.cropContainer">
          <div :class="$style.previewTitle">
            <h2>Crop Image</h2>
          </div>
          <div>
            <Cropper
              ref="cropper"
              class="motto-cropper"
              background-class="motto-cropper__background"
              foreground-class="motto-cropper__foreground"
              image-restriction="stencil"
              :resize-image="{ wheel: false }"
              :stencil-size="stencilSize"
              :stencil-props="{
                lines: {},
                handlers: {},
                movable: false,
                scalable: false,
                aspectRatio: 1,
                previewClass: 'motto-cropper__stencil',
              }"
              :transitions="false"
              :debounce="false"
              :default-size="defaultSize"
              :min-width="150"
              :min-height="150"
              :src="imagePreviewPath"
              @change="onChange" />
            <CropZoomResizer :zoom="zoom" @change="onZoom" />
          </div>
        </div>
        <div class="d-flex flex-column">
          <div :class="[$style.previewTitle, isMobile ? '' : 'ml-20']">
            <h2>Preview</h2>
          </div>
          <div :class="$style.previewColumn">
            <div :class="$style.previewPictureWrapper">
              <Preview
                :height="isMobile ? 150 : 200"
                :width="isMobile ? 150 : 200"
                :image="previewImageResult.image"
                :coordinates="previewImageResult.coordinates" />
            </div>
            <div :class="$style.submitButtonsContainer">
              <BaseButton
                :disabled="isSubmitting"
                theme="red"
                @click="() => this.$bvModal.hide('previewPictureUpload')">
                <p class="button-text color-white">Discard</p>
              </BaseButton>
              <BaseButton :disabled="isSubmitting" theme="blue" @click="submitPicture">
                <LoaderIcon v-if="isSubmitting"></LoaderIcon>
                <p v-else class="button-text color-white">Submit</p>
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss">
@import '@design';

.motto-cropper {
  @media (max-width: $lg-breakpoint) {
    width: min(70vw, 350px);
    max-width: min(70vw, 350px);
    min-height: 200px;
  }

  width: 350px;
  max-width: 350px;
  height: 100%;
  max-height: 500px;
  background: $lightest-gray;

  &__background {
    background-color: $lightest-gray;
  }

  &__foreground {
    background-color: $lightest-gray;
  }

  &__stencil {
    border: solid 5px $primary-blue;
  }
}
</style>

<style lang="scss" module>
@import '@design';

.pictureUploadLabel {
  cursor: pointer;

  input {
    display: none;
  }
}

.cropContainer {
  display: flex;
  flex-direction: column;
}

.previewContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
    width: 100%;
  }

  display: flex;
}

.previewPictureWrapper {
  @media (max-width: $lg-breakpoint) {
    width: 150px;
    height: 150px;
  }

  width: 200px;
  height: 200px;
  overflow: hidden;
  background: $lightest-gray;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 1), 0 0 0 4px $primary-blue;
}

.previewColumn {
  @media (max-width: $lg-breakpoint) {
    justify-content: start;
    min-height: unset;
    margin-left: 0;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  padding-right: 20px;
  padding-left: 20px;
  margin-left: 20px;

  button {
    width: fit-content;
    margin-top: 30px;
  }
}

.penIcon {
  @media (max-width: $lg-breakpoint) {
    svg {
      width: 20px;
    }
  }

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px;
  border-radius: 50%;

  &:hover {
    background: $lightest-gray;
    opacity: 0.9;
  }
}

.submitButtonsContainer {
  display: flex;

  button:first-child {
    margin-right: 5px;
  }
}

.previewTitle {
  @media (max-width: $lg-breakpoint) {
    height: 55px;
  }

  height: 55px;
  text-align: center;

  h2 {
    margin: 0;
  }
}
</style>
